import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StepperDialogComponent } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  AssetsGenerateUrlService,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  PROMPTS,
  StepperModel,
  ValueListsService,
  WidthClass,
} from '@thehubz/client/core';
import { Asset, Collection, CollectionsApiService, CompanyBaseDataWithClients, UpdateCollectionDto, UserInfo } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { lastValueFrom } from 'rxjs';
import { AssetCommonService } from './asset-common.service';

@Injectable({
  providedIn: 'root',
})
export class CollectionEditStepperService {
  brandingKey!: CustomClients;
  CustomClients = CustomClients;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private dialog: MatDialog,
    private commonUtilityLib: CommonUtilityLib,
    private valueListsService: ValueListsService,
    public assetCommonService: AssetCommonService,
    private assetUrlService: AssetsGenerateUrlService,
    protected collectionAPIService: CollectionsApiService,
    private readonly clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.brandingKey = env.brandingKey;
  }
  initStepperData!: StepperModel;
  collectionDataModel!: StepperModel;

  /**
   * Create stepper model for publishing
   * @param stepLabel
   * @returns
   */
  private async createStepperModel(collection: Collection, stepLabel: string): Promise<StepperModel> {
    const listUserAgencies = (await lastValueFrom(this.valueListsService.listUserAgencies()))
      .filter((agency) => agency.clients.length)
      .map((agency) => ({ ...agency, selected: false }));

    const tagIDs = collection.keywords ? collection.keywords : [];
    const tags = tagIDs ? await this.valueListsService.listTags(tagIDs) : [];
    // Define a filter object to retrieve all collection owners' information,
    // excluding the user who created the collection.
    // Retrieve the list of all collection owners from the users API service.
    const allCollectionOwnersList: UserInfo[] = (await lastValueFrom(this.valueListsService.listUsers())).filter((user) =>
      collection.ownerIDs?.some((owner) => user.id === owner),
    );
    // Retrieve the list of users similar to the current user from the value lists service.
    const usersLikeMeList = await lastValueFrom(this.valueListsService.listUsersLikeMe(['global.library'], false, true));
    // Merge the collection owners list with the list of users similar to the current user.
    const usersLikeMe = this.mergeCollectionOwnerWithUsersLikeMe(allCollectionOwnersList, usersLikeMeList);

    let agencyLable = '';
    let clientsLabel = '';

    switch (this.brandingKey) {
      case 'vorwerk': {
        agencyLable = $localize`Domain`;
        clientsLabel = $localize`Location & Brands`;
        break;
      }
      case 'lufthansa-mediacenter':
      case 'lufthansa-medialounge':
      default: {
        agencyLable = $localize`Agency`;
        clientsLabel = $localize`Clients & Brands`;
        break;
      }
    }

    const model: StepperModel = {
      stepID: 'editModel',
      stepLabel: stepLabel,
      stepper: [
        {
          stepperId: '1',
          stepperName: $localize`General information`,
          stepperGroupName: 'collectionMetadata',
          selected: true,
          visited: true,
          disable: true,
          hideHeader: false,
          formFields: [
            {
              formTypeId: '1',
              formFieldName: 'name',
              fieldType: 'text',
              fieldLabel: $localize`Name`,
              placeholder: $localize`Name`,
              inputType: 'text',
              validators: {
                required: true,
              },
              widthClass: WidthClass.W11,
              visible: true,
              value: collection.name,
            },
            {
              formTypeId: '2',
              formFieldName: 'description',
              fieldType: 'textarea',
              fieldLabel: $localize`Description`,
              placeholder: $localize`Description`,
              inputType: 'text',
              validators: {
                required: true,
              },
              widthClass: WidthClass.W11,
              visible: true,
              value: collection.description,
            },
            {
              formTypeId: '3',
              formFieldName: 'cover',
              fieldType: 'dropdown',
              fieldLabel: $localize`Cover`,
              dropDownSearch: true,
              inlineImage: true,
              validators: {
                required: false,
              },
              withImg: true,
              imgPreviewUrl: '',
              placeholder: $localize`Choose cover`,
              widthClass: WidthClass.W11,
              options: collection.assets?.map((asset: AssetUIModel) => {
                return { _id: asset._id, name: asset.name, imageUrl: asset.imageUrl };
              }),
              value: collection.coverAssetID,
              visible: true,
            },
            {
              formTypeId: '4',
              formFieldName: 'owners',
              fieldType: 'multiSelectChip',
              fieldLabel: $localize`Owners`,
              validators: {
                required: false,
              },
              placeholder: $localize`Choose owners`,
              widthClass: WidthClass.W11,
              options: usersLikeMe,
              value: usersLikeMe.filter((user) => collection.ownerIDs?.includes(user.id)),
              visible: true,
              showAvatar: true,
            },
            {
              formTypeId: '5',
              formFieldName: 'keywords',
              fieldType: 'matChipList',
              fieldLabel: $localize`Tags`,
              mandatory: true,
              validators: {
                required: this.brandingKey === CustomClients.Standard ? false : true,
              },
              placeholder: $localize`Enter tags`,
              widthClass: WidthClass.W11,
              options: [],
              value: tags.map((tag) => tag.name),
              visible: true,
            },
          ],
          actions: [
            collection.published?.length
              ? {
                  actionLabel: $localize`:action:Next`,
                  actionType: 'editPublishedCollection',
                  goToStep: 2,
                  actionTypePosition: 'right',
                  icon: 'chevron_right',
                  alignIcon: 'right',
                  disable: false,
                  visible: false,
                }
              : {
                  actionLabel: $localize`:button:Save`,
                  actionTypePosition: 'right',
                  actionType: 'updateCollection',
                  icon: 'save',
                  alignIcon: 'left',
                  visible: false,
                },
          ],
        },
        {
          stepperId: '2',
          stepperName: $localize`Publishing details`,
          stepperGroupName: 'information',
          hideHeader: false,
          selected: false,
          visited: false,
          formFields: [
            {
              formTypeId: '13',
              formFieldName: 'publishCollection',
              fieldType: 'formArray',
              fieldLabel: '',
              placeholder: '',
              inputType: '',
              mandatory: true,
              widthClass: WidthClass.WFull,
              disable: false,
              visible: true,
              formFieldGroup: [
                {
                  formTypeId: '7',
                  formFieldName: 'agency',
                  fieldType: 'dropdown',
                  fieldLabel: agencyLable,
                  placeholder: agencyLable,
                  widthClass: WidthClass.W12,
                  options: listUserAgencies,
                  value: '',
                  validators: {
                    required: true,
                  },
                  visible: true,
                },
                {
                  formTypeId: '8',
                  formFieldName: 'clientNBrand',
                  fieldType: 'treeHierarchy',
                  fieldLabel: clientsLabel,
                  widthClass: WidthClass.W12,
                  validators: {
                    required: true,
                  },
                  visible: true,
                  value: '',
                },
              ],
            },
          ],
          actions: [
            {
              actionLabel: $localize`Save`,
              actionTypePosition: 'right',
              icon: 'save',
              alignIcon: 'left',
              actionType: 'updateCollection',
              disable: false,
              visible: false,
            },
            {
              actionLabel: PROMPTS.LABELS.BACK,
              actionType: 'back',
              goToStep: 1,
              actionTypePosition: 'left',
              icon: 'chevron_left',
              alignIcon: 'left',
              visible: false,
            },
          ],
        },
      ],
    };
    if (!collection.published?.length) {
      model['stepper'] = model.stepper.slice(0, 1);
    }
    return model;
  }

  /**
   * This function merges two arrays of UserInfo objects, allCollectionOwnersList and owners,
   * based on the 'id' property of each object. The resulting array contains all unique users
   * from both input arrays.
   * @param allCollectionOwnersList
   * @param owners
   * @returns
   */
  private mergeCollectionOwnerWithUsersLikeMe(allCollectionOwnersList: UserInfo[], owners: UserInfo[]): UserInfo[] {
    // Create a new Map object to store unique users during the merge process
    const usersMap = new Map();
    // Iterate over the allCollectionOwnersList array and add each user to the Map
    allCollectionOwnersList.forEach((user) => usersMap.set(user.id, user));
    // Iterate over the owners array and add each user to the Map, or update the existing entry
    owners.forEach((user) => {
      const exists = usersMap.has(user.id);
      if (!exists) {
        usersMap.set(user.id, user);
      }
    });

    // Convert the Map object back into an array of UserInfo objects and return it
    return Array.from(usersMap.values());
  }

  /**
   * open stepper dialog for publish collection
   */
  async collectionEditStepper(collection: Collection) {
    const stepLabel = $localize`Edit collection`;
    collection.assets = this.assetUrlService.generatePreview(collection.assets as AssetUIModel[]) as Asset[];
    const stepperData = await this.createStepperModel(collection, stepLabel);
    let collectionData = collection;
    if (collection.assets && collection.assets.length > 0 && typeof collection.assets[0] === 'string') {
      collectionData = await lastValueFrom(this.collectionAPIService.collectionsControllerFindOne(collection._id));
    }
    const collectionPublished = this.transformModel(collection.published || []);
    return new Promise((resolve, reject) => {
      const dialogRef = this.dialog.open(StepperDialogComponent, {
        width: '70%',
        height: '80%',
        maxWidth: '100vw',
        disableClose: true,
        panelClass: 'custom-dialog-container',
        data: {
          stepperData,
          collectionData: collectionData,
          additionalData: collectionPublished,
          callFrom: 'editCollection',
          editable: collection.published?.length ? true : false,
        },
      });
      dialogRef.componentInstance.matTreeCheckbox = true;
      // after dialog close create sharing collection in db
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result && !this.commonUtilityLib.isEmptyObject(result)) {
          const updateObj: UpdateCollectionDto = {};
          if (result.name) updateObj.name = result.name;
          if (result.description) updateObj.description = result.description;
          if (result.keywords) updateObj.keywords = await this.assetCommonService.modifiedTags(result.keywords);
          // if cover available create coverAssetID property
          if (result.cover) updateObj.coverAssetID = result.cover._id;
          // if owners available create ownerIDs property
          if (result.owners) updateObj.ownerIDs = result.owners.map((owner: { id: string }) => owner.id);
          if (result.publish) updateObj.published = Object.values(result.publish);

          this.collectionAPIService.collectionsControllerUpdate(collection._id, updateObj).subscribe({
            next: async (coll) => {
              if (coll) {
                coll.assets = collection.assets;
                const newSelectedCover = collection.assets?.find((asset) => asset._id === result.cover?._id);
                if (newSelectedCover)
                  (coll as AnyType).coverAssetObj = {
                    _id: newSelectedCover._id,
                    name: newSelectedCover?.name,
                    mainFile: newSelectedCover?.mainFile,
                    assetType: newSelectedCover?.assetType,
                  };
                this.clientNotificationHandlerService.successNotification(
                  $localize`Collection "${result.name ?? collection.name}" has been updated successfully`,
                );
                resolve(coll);
              }
            },
            error: (err) => {
              reject(err);
              this.clientNotificationHandlerService.errorNotification(err);
            },
          });
        } else {
          this.clientNotificationHandlerService.successNotification($localize`No changes found to update`);
          resolve(undefined);
        }
      });
    });
  }

  /**
   * Transform collection publish model CompanyBaseDataWithClients to Agency Client and Brand (_id, name) model
   * @param published
   * @returns
   */
  transformModel(published: CompanyBaseDataWithClients[]) {
    if (published.length) {
      return published.map((company) => ({
        _id: company.key,
        name: company.name,
        clients: company.clients?.map((client) => ({
          _id: client.key,
          name: client.name,
          brands: client.brands?.map((brand) => ({ _id: brand.key, name: brand.name })),
        })),
      }));
    }
    return [];
  }

  getPublishDataModel(data: AnyType) {
    return data.map((publish: { agency: { _id: string; name: string; clients: { name: string; _id: string; brands: AnyType[] }[] } }) => ({
      key: publish.agency._id,
      company: publish.agency._id,
      name: publish.agency.name,
      clients: publish.agency.clients.map((client: { name: string; _id: string; brands: AnyType[] }) => ({
        name: client.name,
        key: client._id,
        client: client._id,
        brands: client.brands?.map((brand) => ({ name: brand.name, key: brand._id, brand: brand._id })),
      })),
    }));
  }
}
